import { useGetRealtimeChannelToken } from '@/api/utils/get-realtime-channel-token-query'
import { useBrandContext } from '@/features/brands/brand-context'
import { useCallback } from 'react'
import { useWebSocketSubscription } from '@/hooks/use-websocket-subscription'
import { useQueryClient } from '@tanstack/react-query'
import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import type { BrandFileLabelApplied } from 'api/dist/types/inngest-channels'

export type IFileLabelAppliedPayload = BrandFileLabelApplied

export interface IFileLabelSubscriptionOptions {
    onError?: (error: Error | null) => void
    onMessage?: (data: IFileLabelAppliedPayload) => void
}

export const useFileLabelSubscription = ({ onError, onMessage }: IFileLabelSubscriptionOptions = {}) => {
    const { brand } = useBrandContext()
    const queryClient = useQueryClient()

    const tokenQuery = useGetRealtimeChannelToken({
        channel: brand ? `${brand.uuid}:drive` : undefined,
        topics: ['fileLabelApplied'],
    })

    const { state, error } = useWebSocketSubscription({
        tokenQuery,
        onMessage: useCallback(
            (event: MessageEvent) => {
                try {
                    const message = JSON.parse(event.data)
                    if (message.kind === 'data' && message.topic === 'fileLabelApplied') {
                        // Invalidate all node queries to refresh file labels in the drive
                        queryClient.invalidateQueries({
                            predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                        })

                        onMessage?.(message.data)
                    }
                } catch {
                    onError?.(new Error('Failed to parse WebSocket message'))
                }
            },
            [queryClient, onError, onMessage]
        ),
        onError,
    })

    return {
        error,
        state,
    }
}
